import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AssessmentProduct() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business_name: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.business_name) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/assessment-product/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.detail || 'Failed to create checkout session');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { title: 'AI Accountability Score', desc: 'Overall score and tier classification' },
    { title: 'Gap Analysis', desc: 'Documentation, oversight, risk, and vendor management gaps' },
    { title: 'Governance Findings', desc: 'What oversight exists and what is missing' },
    { title: 'Risk Findings', desc: 'Categorized by severity (High / Medium / Low)' },
    { title: 'Documentation Findings', desc: 'Which documents exist vs. which are missing' },
    { title: '30-Day Action Plan', desc: 'Prioritized actions with effort and impact ratings' },
    { title: 'Declaration Readiness Score', desc: 'Likelihood of passing the Declaration review' },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#102A43] mb-8 text-sm">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Hero */}
          <div className="mb-12">
            <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
              $297 · ONE-TIME PAYMENT
            </p>
            <h1 className="text-[36px] font-bold text-[#102A43] mb-4 leading-tight">
              A complete picture of your organization's AI accountability posture
            </h1>
            <p className="text-[18px] text-[#64748B]">
              Scored findings, documented gaps, and a prioritized 30-day action plan.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* What's Included */}
            <div>
              <h2 className="text-[24px] font-bold text-[#102A43] mb-6">What's Included</h2>
              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
                <ul className="space-y-4">
                  {features.map((feature) => (
                    <li key={feature.title} className="flex items-start gap-3">
                      <CheckCircle className="text-[#27AE60] flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <span className="text-[15px] font-medium text-[#102A43]">{feature.title}</span>
                        <span className="text-[14px] text-[#64748B]"> — {feature.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* How it works */}
              <div className="mt-8">
                <h3 className="text-[18px] font-bold text-[#102A43] mb-4">How It Works</h3>
                <ol className="space-y-3 text-[14px] text-[#64748B]">
                  <li className="flex gap-3">
                    <span className="font-bold text-[#2F80ED]">1.</span>
                    <span>Complete purchase and provide your organization details</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-[#2F80ED]">2.</span>
                    <span>Our team reviews your submission within 24 hours</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-[#2F80ED]">3.</span>
                    <span>Receive your complete assessment report via email</span>
                  </li>
                </ol>
              </div>
            </div>

            {/* Purchase Form */}
            <div>
              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8">
                <p className="text-[28px] font-bold text-[#102A43] mb-1 text-center">
                  $297
                </p>
                <p className="text-[15px] text-[#64748B] mb-6 text-center">
                  One-time payment
                </p>

                <form onSubmit={handleCheckout} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-1">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-1">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                      required
                    />
                  </div>

                  {error && (
                    <p className="text-red-600 text-sm">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#2F80ED] text-white font-semibold py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Get Your Assessment'}
                  </button>
                </form>

                <p className="text-[12px] text-[#94A3B8] mt-4 text-center">
                  Report delivered within 24 hours
                </p>
              </div>

              {/* Disclaimer */}
              <p className="text-[12px] text-[#94A3B8] mt-6 leading-relaxed">
                AASC assessments are operational governance reviews, not legal audits or regulatory approvals. Results should not be relied upon as legal advice.
              </p>
            </div>
          </div>

          {/* Post-purchase upsell note */}
          <div className="mt-12 text-center">
            <p className="text-[14px] text-[#64748B]">
              After your assessment: Ready to declare publicly?{' '}
              <Link to="/apply" className="text-[#2F80ED] hover:underline">
                Start your AASC Accountability Declaration — $997/year
              </Link>
            </p>
          </div>
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default AssessmentProduct;
